import {
  type DailyLogSearchConditions,
  type DateRange,
  getCurrentMonthRange,
  getRecentDaysRange,
  isValidIsoDate,
} from "./dailyLogSearchState";

/**
 * 필터 popover·모바일 panel에서 Apply 전까지 들고 있는 값.
 * 열 때 적용 조건을 복사하고, Cancel·ESC·닫기에서 폐기한다.
 */

export type DateDraft =
  | { mode: "all" }
  | { mode: "recent" }
  | { mode: "month" }
  | { mode: "custom"; startDate: string; endDate: string };

export type DatePresetMode = DateDraft["mode"];

export interface FilterDraft {
  date: DateDraft;
  contractorIds: number[];
  tagIds: number[];
}

function isSameRange(range: DateRange, startDate: string | null, endDate: string | null) {
  return range.startDate === startDate && range.endDate === endDate;
}

/** 적용 조건을 draft로 복사한다. 프리셋과 날짜가 같으면 프리셋으로 표시한다 */
export function toDateDraft(conditions: DailyLogSearchConditions, today: string | null): DateDraft {
  const { startDate, endDate } = conditions;
  if (startDate === null && endDate === null) return { mode: "all" };

  if (today !== null) {
    if (isSameRange(getRecentDaysRange(today), startDate, endDate)) return { mode: "recent" };
    if (isSameRange(getCurrentMonthRange(today), startDate, endDate)) return { mode: "month" };
  }
  return { mode: "custom", startDate: startDate ?? "", endDate: endDate ?? "" };
}

export function toFilterDraft(conditions: DailyLogSearchConditions, today: string | null): FilterDraft {
  return {
    date: toDateDraft(conditions, today),
    contractorIds: conditions.contractorIds,
    tagIds: conditions.tagIds,
  };
}

export type DateDraftResolution =
  | { ok: true; startDate: string | null; endDate: string | null }
  | { ok: false; reason: "incomplete" | "reversed" | "timezone-unavailable" };

/**
 * draft를 적용할 날짜로 바꾼다.
 * 직접 기간은 시작·종료가 모두 유효할 때만 적용한다. 하나만 고른 상태를 자동으로 채우지 않는다.
 */
export function resolveDateDraft(draft: DateDraft, today: string | null): DateDraftResolution {
  switch (draft.mode) {
    case "all":
      return { ok: true, startDate: null, endDate: null };
    case "recent":
    case "month": {
      if (today === null) return { ok: false, reason: "timezone-unavailable" };
      const range = draft.mode === "recent" ? getRecentDaysRange(today) : getCurrentMonthRange(today);
      return { ok: true, ...range };
    }
    case "custom": {
      const isComplete = isValidIsoDate(draft.startDate) && isValidIsoDate(draft.endDate);
      if (!isComplete) return { ok: false, reason: "incomplete" };
      if (draft.startDate > draft.endDate) return { ok: false, reason: "reversed" };
      return { ok: true, startDate: draft.startDate, endDate: draft.endDate };
    }
  }
}
